import React, { useState } from 'react';
import {
  Card, Form, Input, Button, message, Space, Select, DatePicker, Cascader,
  InputNumber, Alert, Divider, Typography, Row, Col, Tag
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { schoolAPI } from '../../services/api';
import { areaData } from '../../utils/areaData';
import { getAreaLabelsByCodes } from '../../utils/areaHelpers';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const STATUS_OPTIONS = [
  { value: 'active', label: '正常运营', color: 'success' },
  { value: 'trial',  label: '试用中',   color: 'processing' },
  { value: 'inactive', label: '停用',   color: 'error' },
];

const PLAN_OPTIONS = [
  { value: 'free',       label: '免费版',   desc: '基础功能，5个模块' },
  { value: 'basic',      label: '基础版',   desc: '扩展功能，8个模块' },
  { value: 'pro',        label: '专业版',   desc: '全部功能，12个模块' },
  { value: 'enterprise', label: '企业版',   desc: '定制服务，无限模块' },
];

const SchoolCreate = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const { area_codes, ...restValues } = values;
      const { province, city, county } = getAreaLabelsByCodes(area_codes);
      const payload = {
        ...restValues,
        province: province || null,
        city: city || null,
        county: county || null,
        expire_date: restValues.expire_date ? restValues.expire_date.format('YYYY-MM-DD') : null,
      };
      const response = await schoolAPI.create(payload);
      if (response.data.success) {
        message.success('学校创建成功！系统已自动初始化默认模块配置。');
        navigate('/schools');
      } else {
        message.error(response.data.message || '创建失败');
      }
    } catch (err) {
      message.error(err.response?.data?.message || '创建学校失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Card
        title={
          <Space>
            <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate('/schools')} style={{ padding: 0 }} />
            <Title level={4} style={{ margin: 0 }}>添加学校</Title>
          </Space>
        }
      >
        <Alert
          message="创建说明"
          description="创建学校后，系统将自动初始化 12 个功能模块的默认配置（5个免费基础模块默认开启，7个高级模块默认关闭）。您可在学校详情页的【模块管理】中进行调整。"
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ status: 'active', plan_type: 'free', max_classes: 50, max_teachers: 200 }}
        >
          <Divider orientation="left">基本信息</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="学校名称" rules={[{ required: true, message: '请输入学校名称' }]}>
                <Input placeholder="请输入学校全称" maxLength={100} showCount />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="area_codes"
                label="所在地区"
                rules={[{ required: true, message: '请选择学校所在的省市县' }]}
              >
                <Cascader options={areaData} placeholder="请选择省 / 市 / 县" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="address" label="详细地址">
                <Input placeholder="请输入学校详细地址" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="principal" label="联系人（校长/负责人）">
                <Input placeholder="联系人姓名" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="phone" label="联系电话">
                <Input placeholder="联系电话" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="email" label="邮箱">
                <Input placeholder="学校邮箱（可选）" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">服务配置</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="status" label="初始状态">
                <Select>
                  {STATUS_OPTIONS.map(opt => (
                    <Option key={opt.value} value={opt.value}>
                      <Tag color={opt.color}>{opt.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="plan_type" label="套餐类型">
                <Select>
                  {PLAN_OPTIONS.map(opt => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                      <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>{opt.desc}</Text>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="expire_date" label="到期日期（留空为永久）">
                <DatePicker style={{ width: '100%' }} placeholder="选择到期日期（可选）" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="max_classes" label="最大班级数">
                <InputNumber min={1} max={9999} style={{ width: '100%' }} addonAfter="个班" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="max_teachers" label="最大教师数">
                <InputNumber min={1} max={99999} style={{ width: '100%' }} addonAfter="位教师" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="备注（管理员内部备注，对学校不可见）">
            <TextArea rows={2} placeholder="可填写合同信息、沟通记录等（可选）" maxLength={500} showCount />
          </Form.Item>

          <Form.Item style={{ marginTop: 24 }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading} icon={<PlusOutlined />} size="large">
                创建学校
              </Button>
              <Button size="large" onClick={() => navigate('/schools')}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </Space>
  );
};

export default SchoolCreate;
